import type { Exercise, PlanExercise, SetEntry, TrackingMode, WorkoutPlan, WorkoutSession, SessionExercise } from './types';

export const modeLabels: Record<TrackingMode, string> = {
  weighted: 'Beban + repetisi', reps: 'Repetisi', duration: 'Durasi', 'weighted-duration': 'Beban + durasi',
};
export const typeLabels = { strength: 'Kekuatan', cardio: 'Kardio', mobility: 'Mobilitas' };
export const locationLabels = { home: 'Rumah', gym: 'Gym', other: 'Lainnya', either: 'Rumah / gym' };
export const weekdays = ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'];
export const formatDate = (value: string) => new Date(value).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' });
export const clockText = (seconds: number) => `${Math.floor(Math.max(0, seconds) / 60)}:${String(Math.floor(Math.max(0, seconds) % 60)).padStart(2, '0')}`;
export const numberText = (value: number) => value.toLocaleString('id-ID', { maximumFractionDigits: 1 });
export const isTimed = (mode: TrackingMode) => mode === 'duration' || mode === 'weighted-duration';
export const isWeighted = (mode: TrackingMode) => mode === 'weighted' || mode === 'weighted-duration';

export function trackingMode(exercise?: Exercise, item?: PlanExercise | SessionExercise): TrackingMode {
  if (item && 'trackingMode' in item && item.trackingMode) return item.trackingMode;
  if (exercise?.trackingMode) return exercise.trackingMode;
  const timed = exercise?.exerciseType === 'cardio' || exercise?.exerciseType === 'mobility'
    || exercise?.id === 'home-side-plank' || exercise?.id === 'home-suitcase-carry'
    || (item && 'targetDurationSeconds' in item && Number(item.targetDurationSeconds) > 0)
    || (item && 'sets' in item && item.sets.some(set => Number(set.durationSeconds) > 0));
  const weighted = exercise ? /dumbbell|barbell|cable|machine|kettlebell/i.test(exercise.equipment) : false;
  return timed ? weighted ? 'weighted-duration' : 'duration' : weighted ? 'weighted' : 'reps';
}

export function setValid(set: SetEntry, mode: TrackingMode) {
  const value = isTimed(mode) ? set.durationSeconds : set.reps;
  return Number.isFinite(value) && Number(value) > 0 && Number(value) <= (isTimed(mode) ? 86400 : 1000)
    && (isTimed(mode) || Number.isInteger(value))
    && (!isWeighted(mode) || (set.weightKg !== null && Number.isFinite(set.weightKg) && set.weightKg >= 0 && set.weightKg <= 2000));
}

export function elapsedSeconds(session: WorkoutSession, now = Date.now()) {
  if (session.elapsedSeconds !== undefined) {
    return Math.max(0, session.elapsedSeconds + (session.status === 'active' && session.runningSince
      ? Math.floor((now - new Date(session.runningSince).getTime()) / 1000) : 0));
  }
  return Math.max(0, Math.floor(((session.finishedAt ? new Date(session.finishedAt).getTime() : now) - new Date(session.startedAt).getTime()) / 1000));
}
export const sessionSets = (s: WorkoutSession) => s.exercises.flatMap(e => e.sets).filter(set => set.completed);
export const sessionVolume = (s: WorkoutSession) => sessionSets(s).reduce((sum, set) => sum + Math.max(0, set.weightKg ?? 0) * Math.max(0, set.reps ?? 0), 0);
export const sessionXp = (s: WorkoutSession) => sessionSets(s).length ? 120 + sessionSets(s).length * 8 : 0;

export function completeExerciseSets(exercise: SessionExercise, mode: TrackingMode, completedAt = new Date().toISOString()): SessionExercise {
  const remaining=exercise.sets.filter(set=>!set.completed);
  if(!remaining.length)return exercise;
  if(remaining.some(set=>!setValid(set,mode)))throw new Error('Isi hasil semua set yang belum selesai terlebih dahulu. Tidak ada set yang diubah.');
  return {...exercise,sets:exercise.sets.map(set=>set.completed?set:{...set,completed:true,completedAt})};
}

export function defaultPlanItem(exercise: Exercise, index: number): PlanExercise {
  const timed = isTimed(trackingMode(exercise));
  return { id: crypto.randomUUID(), exerciseId: exercise.id, sortOrder: index, targetSets: timed ? 1 : 3,
    targetReps: timed ? null : 10, targetDurationSeconds: timed ? exercise.exerciseType === 'cardio' ? 300 : 30 : null, targetRestSeconds: timed ? 30 : 90 };
}
export function defaultSet(mode: TrackingMode, setNumber: number, template?: Partial<SetEntry>): SetEntry {
  return { id: crypto.randomUUID(), setNumber, reps: isTimed(mode) ? null : template?.reps ?? 10,
    durationSeconds: isTimed(mode) ? template?.durationSeconds ?? 30 : null,
    weightKg: isWeighted(mode) ? template?.weightKg ?? null : null, completed: false };
}
export function planError(plan: WorkoutPlan, catalog: Exercise[]) {
  if (plan.name.trim().length < 3) return 'Nama program minimal 3 karakter.';
  if (!Number.isFinite(plan.targetDurationMinutes) || Number(plan.targetDurationMinutes) < 1 || Number(plan.targetDurationMinutes) > 600) return 'Target durasi harus 1–600 menit.';
  if (!plan.exercises.length) return 'Tambahkan minimal satu gerakan sebelum menyimpan program.';
  for (const item of plan.exercises) {
    const exercise = catalog.find(e => e.id === item.exerciseId);
    if (!exercise) return 'Ada gerakan yang tidak tersedia. Ganti atau keluarkan gerakan tersebut.';
    if (!Number.isInteger(item.targetSets) || item.targetSets < 1 || item.targetSets > 100) return `Jumlah set ${exercise.name} harus 1–100.`;
    if (!Number.isFinite(item.targetRestSeconds) || item.targetRestSeconds < 0 || item.targetRestSeconds > 3600) return 'Jeda harus 0–3600 detik.';
    const timed = isTimed(trackingMode(exercise, item));
    const target = timed ? item.targetDurationSeconds : item.targetReps;
    if (!Number.isFinite(target) || Number(target) <= 0 || Number(target) > (timed ? 86400 : 1000) || (!timed && !Number.isInteger(target))) return `Isi target ${timed ? 'durasi' : 'repetisi'} yang valid untuk ${exercise.name}.`;
  }
  return '';
}
export function buildSession(plan: WorkoutPlan | undefined, catalog: Exercise[], history: WorkoutSession[]): WorkoutSession {
  const now = new Date().toISOString();
  return { id: crypto.randomUUID(), planId: plan?.id, name: plan?.name ?? 'Latihan bebas', startedAt: now, updatedAt: now,
    elapsedSeconds: 0, status: 'active', locationType: plan?.locationType && plan.locationType !== 'either' ? plan.locationType : 'home',
    exercises: [...(plan?.exercises ?? [])].sort((a,b) => a.sortOrder-b.sortOrder).map((item, index) => {
      const exercise = catalog.find(e => e.id === item.exerciseId);
      const mode = trackingMode(exercise, item);
      const previous = history.filter(s => s.status === 'completed').sort((a,b) => b.startedAt.localeCompare(a.startedAt))
        .flatMap(s => s.exercises).find(e => e.exerciseId === item.exerciseId)?.sets.filter(s => s.completed);
      return { id: crypto.randomUUID(), exerciseId: item.exerciseId, exerciseName: exercise?.name ?? 'Gerakan',
        sortOrder: index, trackingMode: mode, restSeconds: item.targetRestSeconds,
        sets: Array.from({ length: item.targetSets }, (_, i) => defaultSet(mode, i+1, {
          reps: item.targetReps, durationSeconds: item.targetDurationSeconds, weightKg: previous?.[i]?.weightKg ?? previous?.[0]?.weightKg ?? null,
        })) };
    }) };
}
